import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Layout } from '../components/layout/Layout';
import { QualificationBadge } from '../components/ui/Badge';
import { TripDetailModal } from '../components/trips/TripDetailModal';
import { usersApi } from '../api/users.api';
import { tripsApi } from '../api/trips.api';
import { User } from '../types/user';
import { Trip } from '../types/trip';
import { useAuth } from '../hooks/useAuth';
import { formatDate, formatTime, formatDuration, formatDistance } from '../utils/formatters';

// ---------------------------------------------------------------------------
// Skeleton loader
// ---------------------------------------------------------------------------

const Skeleton: React.FC<{ className?: string }> = ({ className = '' }) => (
  <div className={`animate-pulse bg-gray-200 rounded ${className}`} />
);

// ---------------------------------------------------------------------------
// Info field helper
// ---------------------------------------------------------------------------

const InfoField: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div className="bg-gray-50 rounded-lg px-4 py-3">
    <p className="text-xs text-gray-500 mb-0.5">{label}</p>
    <p className="text-sm font-medium text-gray-900">{value}</p>
  </div>
);

// ---------------------------------------------------------------------------
// Stat card helper
// ---------------------------------------------------------------------------

const StatCard: React.FC<{ label: string; value: React.ReactNode; colorClass?: string }> = ({
  label,
  value,
  colorClass = 'text-gray-900',
}) => (
  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
    <p className="text-sm text-gray-500">{label}</p>
    <p className={`text-2xl font-bold mt-1 ${colorClass}`}>{value}</p>
  </div>
);

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export const DriverDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  useTranslation(); // keep i18n context active (QualificationBadge uses it)
  const { user: currentUser } = useAuth();

  const isSuperAdmin = currentUser?.isSuperAdmin === true || currentUser?.role === 'super_admin';
  const canViewGps =
    isSuperAdmin ||
    currentUser?.role === 'driver' ||
    currentUser?.company?.gps_data_visible_to_company_users === true;

  const [driver, setDriver] = useState<User | null>(null);
  const [driverLoading, setDriverLoading] = useState(true);
  const [driverError, setDriverError] = useState<string | null>(null);

  // All trips loaded for stats computation (first page large limit to get totals)
  const [allTrips, setAllTrips] = useState<Trip[]>([]);
  const [totalTrips, setTotalTrips] = useState(0);

  const [trips, setTrips] = useState<Trip[]>([]);
  const [tripsLoading, setTripsLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const LIMIT = 10;

  const [selectedTrip, setSelectedTrip] = useState<Trip | null>(null);

  const loadDriver = useCallback(async () => {
    if (!id) return;
    setDriverLoading(true);
    setDriverError(null);
    try {
      const data = await usersApi.getUser(id);
      setDriver(data);
    } catch {
      setDriverError('Impossible de charger ce conducteur.');
    } finally {
      setDriverLoading(false);
    }
  }, [id]);

  // Load a large first page to compute aggregate stats from current data.
  // The pagination total gives us the true total count.
  const loadStatsTrips = useCallback(async () => {
    if (!id) return;
    try {
      const result = await tripsApi.getTrips({ driverId: id, page: 1, limit: 100 });
      setAllTrips(Array.isArray(result.data) ? result.data : []);
      setTotalTrips(result.pagination?.total ?? 0);
    } catch {
      setAllTrips([]);
    }
  }, [id]);

  const loadTrips = useCallback(async () => {
    if (!id) return;
    setTripsLoading(true);
    try {
      const result = await tripsApi.getTrips({ driverId: id, page, limit: LIMIT });
      setTrips(Array.isArray(result.data) ? result.data : []);
      setTotalPages(result.pagination?.totalPages ?? 1);
    } catch {
      setTrips([]);
    } finally {
      setTripsLoading(false);
    }
  }, [id, page]);

  useEffect(() => {
    loadDriver();
  }, [loadDriver]);

  useEffect(() => {
    if (driver) {
      loadStatsTrips();
      loadTrips();
    }
  }, [driver, loadStatsTrips, loadTrips]);

  const getInitials = (name: string) =>
    name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);

  // Aggregate stats derived from allTrips
  const totalDistanceKm = allTrips.reduce((sum, t) => sum + (t.distance_km ?? 0), 0);
  const pendingCount = allTrips.filter((t) => t.qualification === 'pending').length;

  return (
    <Layout>
      <div className="space-y-6">
        {/* Back button */}
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 transition-colors"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Retour
        </button>

        {/* Driver info card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          {driverLoading ? (
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <Skeleton className="h-16 w-16 rounded-full" />
                <div className="space-y-2">
                  <Skeleton className="h-6 w-48" />
                  <Skeleton className="h-4 w-32" />
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-4">
                {[...Array(5)].map((_, i) => (
                  <Skeleton key={i} className="h-14" />
                ))}
              </div>
            </div>
          ) : driverError ? (
            <div className="text-center py-8">
              <p className="text-red-600">{driverError}</p>
              <button
                onClick={loadDriver}
                className="mt-3 text-sm text-primary-600 hover:underline"
              >
                Réessayer
              </button>
            </div>
          ) : driver ? (
            <div>
              {/* Avatar + name */}
              <div className="flex flex-col sm:flex-row sm:items-start gap-4 mb-6">
                <div className="flex-shrink-0 w-16 h-16 rounded-full bg-green-100 flex items-center justify-center">
                  <span className="text-green-700 font-bold text-xl">
                    {getInitials(driver.name)}
                  </span>
                </div>
                <div>
                  <div className="flex items-center gap-3 flex-wrap">
                    <h1 className="text-2xl font-bold text-gray-900">{driver.name}</h1>
                    <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-800">
                      Conducteur
                    </span>
                    <span
                      className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                        driver.status === 'active'
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      }`}
                    >
                      {driver.status === 'active' ? 'Actif' : 'Inactif'}
                    </span>
                  </div>
                  <p className="text-gray-500 mt-1">{driver.email}</p>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <InfoField label="Email" value={driver.email} />
                <InfoField label="Téléphone" value={driver.phone || '—'} />
                <InfoField
                  label="Statut"
                  value={driver.status === 'active' ? 'Actif' : 'Inactif'}
                />
                <InfoField label="Membre depuis" value={formatDate(driver.createdAt)} />
                {(driver as any).department && (
                  <InfoField label="Département" value={(driver as any).department} />
                )}
                {driver.company && (
                  <InfoField label="Société" value={driver.company.name} />
                )}
              </div>
            </div>
          ) : null}
        </div>

        {/* Stats row */}
        {!driverLoading && driver && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <StatCard label="Trajets totaux" value={totalTrips} />
            <StatCard
              label="Distance totale"
              value={`${totalDistanceKm.toFixed(1)} km`}
              colorClass="text-indigo-600"
            />
            <StatCard
              label="Trajets en attente"
              value={pendingCount}
              colorClass={pendingCount > 0 ? 'text-amber-600' : 'text-gray-900'}
            />
          </div>
        )}

        {/* Trips table */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100">
            <h2 className="text-base font-semibold text-gray-900">Trajets</h2>
          </div>

          {driverLoading ? (
            <div className="p-6 space-y-3">
              {[...Array(4)].map((_, i) => (
                <Skeleton key={i} className="h-12" />
              ))}
            </div>
          ) : tripsLoading ? (
            <div className="p-6 space-y-3">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-12" />
              ))}
            </div>
          ) : trips.length === 0 ? (
            <div className="p-12 text-center">
              <p className="text-gray-500">Aucun trajet enregistré</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date de début
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Durée
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Distance
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Qualification
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {trips.map((trip) => (
                    <tr key={trip._id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {formatDate(trip.start_time)}
                        </div>
                        <div className="text-xs text-gray-500">{formatTime(trip.start_time)}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                        {formatDuration(trip.duration_minutes)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                        {formatDistance(trip.distance_km)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <QualificationBadge type={trip.qualification} />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <button
                          onClick={() => setSelectedTrip(trip)}
                          className="text-gray-400 hover:text-primary-600 transition-colors"
                          title="Voir le détail"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                            />
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                            />
                          </svg>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {!tripsLoading && !driverLoading && totalPages > 1 && (
            <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between">
              <p className="text-sm text-gray-500">
                Page {page} sur {totalPages}
              </p>
              <div className="flex gap-2">
                <button
                  disabled={page <= 1}
                  onClick={() => setPage((p) => p - 1)}
                  className="px-3 py-1.5 text-sm rounded-md border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  Précédent
                </button>
                <button
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => p + 1)}
                  className="px-3 py-1.5 text-sm rounded-md border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  Suivant
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Trip detail modal */}
      {selectedTrip && (
        <TripDetailModal
          trip={selectedTrip}
          canViewGps={canViewGps}
          onClose={() => setSelectedTrip(null)}
        />
      )}
    </Layout>
  );
};
